import { useParams, Link, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";
import { PRIVATE_NOTICE } from "@/constants/ingredients";
import { BRAND } from "@/constants/brand";

const AMBIENT = {
  water: ["☁️","🪁"], milk: ["🥛","✨"], creamy: ["🍫","✨"],
  innovation: ["🧪","💡"], zero_sugar: ["🌿","🍃"],
};

const STATUS_META = {
  available:    { label: "Available now",    tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  coming_soon:  { label: "Coming Soon",      tone: "bg-blue-100 text-blue-800 border-blue-300" },
  seasonal:     { label: "Seasonal",         tone: "bg-amber-100 text-amber-800 border-amber-300" },
  limited:      { label: "Limited Edition",  tone: "bg-rose-100 text-rose-800 border-rose-300" },
  out_of_stock: { label: "Out of Stock",     tone: "bg-zinc-200 text-zinc-700 border-zinc-300" },
};

export default function CategoryWorld() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [flavors, setFlavors] = useState([]);
  const [others, setOthers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setW(null); setFlavors([]); setNotFound(false);
    api.get(`/worlds/${slug}`).then((r) => setW(r.data)).catch(() => setNotFound(true));
    api.get(`/worlds/${slug}/flavors`).then((r) => setFlavors(r.data || [])).catch(() => setFlavors([]));
    api.get("/worlds").then((r) => setOthers((r.data || []).filter((x) => x.slug !== slug)));
  }, [slug]);

  if (notFound) return <Navigate to="/worlds" replace />;
  if (!w) return <div className="section-pad container-pp text-brand-charcoal/60">Loading world…</div>;

  const isDark = !!w.dark;
  const text = isDark ? "text-brand-cream" : "text-brand-charcoal";
  const muted = isDark ? "text-brand-cream/70" : "text-brand-charcoal/70";
  const ambient = AMBIENT[slug] || ["✨","✨"];

  return (
    <div data-testid={`world-${slug}`} style={{ background: w.bg }} className="min-h-screen">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {w.video_url ? (
            <video src={w.video_url} autoPlay muted loop playsInline className="h-full w-full object-cover" />
          ) : (
            w.hero && <img src={w.hero} alt={w.label} className="h-full w-full object-cover" />
          )}
          <div className={`absolute inset-0 ${isDark ? "bg-brand-charcoal/55" : "bg-brand-cream/55"}`} />
        </div>
        <div className="relative container-pp px-6 lg:px-10 py-32 lg:py-48">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em]" style={{ color: isDark ? "#FFC93C" : "#FF4F5A" }}>
            <Sparkles size={14} /> {w.theme}
          </div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
            className={`editorial-h1 text-5xl sm:text-7xl md:text-8xl mt-4 ${text} max-w-4xl`}>{w.headline}</motion.h1>
          <p className={`mt-8 max-w-xl text-lg ${muted}`}>{w.sub}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" className="btn-pill btn-primary" data-testid={`world-${slug}-wa`}>WhatsApp the store</a>
            <Link to="/flavour-creator" className={`btn-pill ${isDark ? "btn-dark" : "btn-outline-dark"}`} data-testid={`world-${slug}-create`}>Invent a flavour <ArrowUpRight size={16} className="arrow" /></Link>
          </div>
        </div>
        <div className="absolute top-12 right-12 text-3xl animate-float-y opacity-70">{ambient[0]}</div>
        <div className="absolute bottom-20 right-32 text-2xl animate-float-y opacity-60" style={{ animationDelay: "2s" }}>{ambient[1]}</div>
      </section>

      {/* Flavour roster */}
      {flavors.length > 0 && (
        <section className="px-6 lg:px-10 pt-16 pb-8">
          <div className="container-pp">
            <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 ${text}`}>
              <div>
                <div className={`text-[11px] uppercase tracking-[0.22em] ${muted}`}>Inside this world</div>
                <h2 className="font-display text-4xl md:text-5xl mt-3 leading-none">{flavors.length} flavour{flavors.length !== 1 ? "s" : ""} in rotation.</h2>
              </div>
              <div className={`text-sm ${muted} max-w-md`}>Availability rotates with season, supply and our lab. Status badges below are live.</div>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`world-${slug}-flavors`}>
              {flavors.map((f, i) => {
                const sm = STATUS_META[f.status] || STATUS_META.available;
                const oos = f.status === "out_of_stock";
                return (
                  <motion.article key={f.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
                    className={`rounded-3xl overflow-hidden bg-white/85 backdrop-blur-sm ${oos ? "opacity-65" : ""}`}
                    data-testid={`flavor-${f.id}`}>
                    <div className="aspect-[4/3] overflow-hidden bg-brand-muted relative">
                      {f.image_url
                        ? <img src={f.image_url} alt={f.name} className={`h-full w-full object-cover ${oos ? "" : "hover:scale-105 transition-transform duration-700"}`} />
                        : <div className="h-full w-full grid place-items-center text-4xl" style={{ background: f.color_hex + "22" }}>🍦</div>}
                      <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] border rounded-full px-2.5 py-1 ${sm.tone}`} data-testid={`flavor-status-${f.id}`}>{sm.label}</span>
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display text-2xl leading-tight text-brand-charcoal">{f.name}</h3>
                        <span className="inline-block w-3 h-3 rounded-full shrink-0 mt-2" style={{ background: f.color_hex }} />
                      </div>
                      {f.description && <p className="mt-2 text-sm text-brand-charcoal/70 leading-relaxed line-clamp-3">{f.description}</p>}
                      {f.ingredients?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {f.ingredients.slice(0, 5).map((ing, k) => (
                            <span key={k} className="text-[10px] uppercase tracking-[0.15em] text-brand-charcoal/55 border border-brand-charcoal/10 rounded-full px-2 py-0.5">{ing}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="px-6 lg:px-10 py-16">
        <div className={`container-pp max-w-3xl mx-auto text-center ${text}`}>
          <div className="text-[11px] uppercase tracking-[0.22em] mb-3" style={{ color: isDark ? "#FFC93C" : "#FF4F5A" }}>A note from our freezer</div>
          <p className="font-display text-3xl sm:text-4xl leading-tight">{PRIVATE_NOTICE.long}</p>
        </div>
      </section>

      {w.vibes?.length > 0 && (
        <section className="px-6 lg:px-10 pb-24">
          <div className="container-pp grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {w.vibes.map((v, i) => (
              <motion.figure key={i} data-testid={`world-${slug}-vibe-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.08, duration: 0.6 }} className="rounded-3xl overflow-hidden bg-white/40 backdrop-blur-sm">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={v.img} alt={v.caption} className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                {v.caption && <figcaption className={`p-4 ${muted} font-display text-lg`}>{v.caption}</figcaption>}
              </motion.figure>
            ))}
          </div>
        </section>
      )}

      <section className={`px-6 lg:px-10 py-20 border-t ${isDark ? "border-white/10" : "border-brand-charcoal/10"}`}>
        <div className="container-pp">
          <div className={`text-[11px] uppercase tracking-[0.22em] ${muted}`}>Other Worlds</div>
          <div className="mt-6 flex flex-wrap gap-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/worlds/${o.slug}`} data-testid={`world-link-${o.slug}`} className={`btn-pill ${isDark ? "btn-primary" : "btn-outline-dark"} text-sm`}>
                {o.label} <ArrowUpRight size={14} className="arrow" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
